import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Sparkles, Radio } from "lucide-react";
import { usePlayer } from "../context/PlayerContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// A drop is treated as "just LIVE" for 90 minutes after its release_at moment.
const LIVE_WINDOW_MS = 90 * 60 * 1000;
// Below this many ms until drop, the ticker gently pulses to build launch tension.
const PULSE_WINDOW_MS = 60 * 60 * 1000;

// Shared CSS for both animated states — kept module-scoped so both branches
// can render the same block without duplicating source. Only one <style> ends
// up in the DOM at any time (either the LIVE branch or the pulsing branch).
const TICKER_KEYFRAMES = `
    @keyframes tickerPulse {
        0%, 100% { transform: scale(1);   box-shadow: 0 10px 28px rgba(212,175,55,0.35); }
        50%      { transform: scale(1.03); box-shadow: 0 14px 40px rgba(212,175,55,0.55); }
    }
    @keyframes tickerLive {
        0%, 100% { box-shadow: 0 10px 28px rgba(212,175,55,0.45); }
        50%      { box-shadow: 0 14px 40px rgba(212,175,55,0.75); }
    }
    .animate-ticker-pulse { animation: tickerPulse 2.4s ease-in-out infinite; }
    .animate-ticker-live  { animation: tickerLive  1.8s ease-in-out infinite; }
    @media (prefers-reduced-motion: reduce) {
        .animate-ticker-pulse,
        .animate-ticker-live { animation: none; transform: none; }
    }
`;

// "3d · 14h" / "22h · 15m" / "45m" / "" if past.
const formatCountdown = (iso, now) => {
    if (!iso) return "";
    const target = new Date(iso).getTime();
    if (Number.isNaN(target)) return "";
    const diff = target - now;
    if (diff <= 0) return "";
    const totalMinutes = Math.floor(diff / 60000);
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;
    if (days > 0) return `${days}d · ${hours}h`;
    if (hours > 0) return `${hours}h · ${minutes}m`;
    return `${minutes}m`;
};

/**
 * Pinned in the site header. Three states, driven by the season schedule:
 *   1. Upcoming    → "NEXT DROP · Xd·Yh" (gently pulses when <1h remains)
 *   2. Just LIVE   → bright "Episode 0X is LIVE — Listen now" (plays audio inline)
 *   3. Idle        → hidden (nothing upcoming, no fresh drop)
 */
export const NextDropTicker = () => {
    const [episodes, setEpisodes] = useState([]);
    const [now, setNow] = useState(() => Date.now());
    const { playEpisode } = usePlayer();

    useEffect(() => {
        let mounted = true;
        axios
            .get(`${API}/episodes`)
            .then((r) => mounted && setEpisodes(r.data || []))
            .catch(() => {});
        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        // Tick faster (10s) during the final hour so the last "Xm" ticks feel alive
        // but coast (60s) otherwise to keep this cheap.
        const id = setInterval(() => setNow(Date.now()), 10_000);
        return () => clearInterval(id);
    }, []);

    // Find (a) most recent episode still inside the LIVE window, and
    //      (b) earliest upcoming episode with a release_at.
    const { justLive, upcoming } = useMemo(() => {
        let live = null;
        let up = null;
        for (const ep of episodes) {
            if (!ep.release_at) continue;
            const t = new Date(ep.release_at).getTime();
            if (Number.isNaN(t)) continue;
            const delta = now - t;
            if (delta >= 0 && delta <= LIVE_WINDOW_MS) {
                // pick the most recent live drop
                if (!live || t > new Date(live.release_at).getTime()) live = ep;
            } else if (delta < 0) {
                // pick the earliest upcoming drop
                if (!up || t < new Date(up.release_at).getTime()) up = ep;
            }
        }
        return { justLive: live, upcoming: up };
    }, [episodes, now]);

    // ---- STATE 2 · Just LIVE ------------------------------------------------
    if (justLive) {
        const num = String(justLive.number).padStart(2, "0");
        const canPlay = !!justLive.audio_url;
        const handleClick = (e) => {
            if (canPlay) {
                e.preventDefault();
                playEpisode(justLive);
                const target = document.getElementById("episodes");
                if (target) target.scrollIntoView({ behavior: "smooth" });
            }
        };
        return (
            <a
                href="#episodes"
                onClick={handleClick}
                data-testid="next-drop-ticker-live"
                aria-label={`Episode ${num} is live — listen now`}
                className="hidden lg:inline-flex items-center gap-2 h-10 pl-3 pr-4 rounded-full bg-gold hover:bg-gold-deep text-white text-xs font-bold tracking-tight shadow-[0_10px_28px_rgba(212,175,55,0.45)] transition-all animate-ticker-live whitespace-nowrap"
            >
                <span className="grid place-items-center w-6 h-6 rounded-full bg-white/25 text-white shrink-0">
                    <Radio className="w-3 h-3" />
                </span>
                <span className="uppercase tracking-[0.14em] text-[10px]">Ep {num}</span>
                <span className="tabular-nums">· LIVE</span>
                <span className="hidden xl:inline text-white/90 font-medium">· Listen now</span>
                <style>{TICKER_KEYFRAMES}</style>
            </a>
        );
    }

    // ---- STATE 1 · Upcoming -------------------------------------------------
    if (!upcoming) return null;
    const countdown = formatCountdown(upcoming.release_at, now);
    if (!countdown) return null;

    const msUntil = new Date(upcoming.release_at).getTime() - now;
    const pulsing = msUntil <= PULSE_WINDOW_MS;

    return (
        <a
            href="#episodes"
            data-testid="next-drop-ticker"
            aria-label={`Next episode drops in ${countdown}`}
            className={`hidden lg:inline-flex items-center gap-2 h-10 pl-3 pr-4 rounded-full bg-white/80 hover:bg-white border text-lilac-900 text-xs font-semibold tracking-tight transition-all ${
                pulsing
                    ? "border-gold ring-2 ring-gold/40 shadow-[0_10px_28px_rgba(212,175,55,0.35)] animate-ticker-pulse"
                    : "border-gold/40 shadow-[0_6px_18px_rgba(212,175,55,0.15)] hover:shadow-[0_10px_28px_rgba(212,175,55,0.25)]"
            }`}
        >
            <span className="grid place-items-center w-6 h-6 rounded-full bg-gold text-white shrink-0">
                <Sparkles className="w-3 h-3" />
            </span>
            <span className="text-lilac-700 uppercase tracking-[0.14em] text-[10px]">
                {pulsing ? "Dropping in" : "Next drop"}
            </span>
            <span
                data-testid="next-drop-countdown"
                className="text-gold-deep font-bold tabular-nums"
            >
                {countdown}
            </span>
            {pulsing && <style>{TICKER_KEYFRAMES}</style>}
        </a>
    );
};
